import { useRef, useState, useEffect } from "react";
import { motion, useReducedMotion, useScroll, useTransform } from "framer-motion";
import { useNavigate } from "react-router-dom";
import {
  Calendar, MapPin, Plane, BedDouble, Lock, ArrowRight, Check,
} from "lucide-react";
import FsLogo from "@/components/FsLogo";
import summitImg from "@/assets/image.jpg";

/* ============ Countdown ============ */
const DEPARTURE = new Date("2027-02-05T00:00:00");

function calc(target) {
  const diff = target - new Date();
  if (diff <= 0) return { days: 0, hours: 0, minutes: 0, seconds: 0, arrived: true };
  return {
    days: Math.floor(diff / 86400000),
    hours: Math.floor((diff / 3600000) % 24),
    minutes: Math.floor((diff / 60000) % 60),
    seconds: Math.floor((diff / 1000) % 60),
    arrived: false,
  };
}
function useCountdown(target) {
  const [t, setT] = useState(() => calc(target));
  useEffect(() => {
    const id = setInterval(() => setT(calc(target)), 1000);
    return () => clearInterval(id);
  }, [target]);
  return t;
}

/* ============ Motion ============ */
const fadeUp = {
  hidden: { opacity: 0, y: 22 },
  visible: (i = 0) => ({
    opacity: 1, y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] },
  }),
};

/* ============ Classified redaction with decrypt-on-hover ============ */
const GLYPHS = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789#%/?█▓▒X";

function Classified({ label = "CLASSIFICATO", className = "" }) {
  const reduce = useReducedMotion();
  const [text, setText] = useState(label);
  const timer = useRef(null);

  const scramble = () => {
    if (reduce) return;
    let frame = 0;
    clearInterval(timer.current);
    timer.current = setInterval(() => {
      frame += 1;
      setText(
        label
          .split("")
          .map((c) => (c === " " ? " " : GLYPHS[Math.floor(Math.random() * GLYPHS.length)]))
          .join("")
      );
      if (frame > 9) {
        clearInterval(timer.current);
        setText(label);
      }
    }, 55);
  };

  useEffect(() => () => clearInterval(timer.current), []);

  return (
    <span
      className={`redaction ${className}`}
      onMouseEnter={scramble}
      onFocus={scramble}
      tabIndex={0}
      aria-label="Destinazione classificata. Accesso negato."
      title="Accesso negato"
    >
      {text}
    </span>
  );
}

/* ===================================================== */
/* PAGE                                                  */
/* ===================================================== */
export default function LandingPage() {
  const detailsRef = useRef(null);
  const countdown = useCountdown(DEPARTURE);

  const scrollTo = (ref) =>
    ref.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  return (
    <div className="bg-ink text-bone">
      <Hero onDetails={() => scrollTo(detailsRef)} />
      <Destination sectionRef={detailsRef} />
      <MissionClock countdown={countdown} />
      <Briefing />
      <Footer />
    </div>
  );
}

/* ===================================================== */
/* HERO                                                  */
/* ===================================================== */
function Hero({ onDetails }) {
  const reduce = useReducedMotion();
  const navigate = useNavigate();
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const glowY = useTransform(scrollYProgress, [0, 1], ["0%", "30%"]);

  return (
    <section
      ref={heroRef}
      data-testid="hero-section"
      className="relative min-h-[100dvh] flex flex-col overflow-hidden grid-bg spotlight"
    >
      <motion.div
        aria-hidden="true"
        className="absolute inset-0 pointer-events-none"
        style={reduce ? {} : { y: glowY }}
      >
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse 70% 50% at 50% 12%, rgba(201,164,122,0.16), transparent 60%)",
          }}
        />
      </motion.div>

      {/* Top bar */}
      <header className="relative z-10 w-full max-w-[1280px] mx-auto px-6 sm:px-10 pt-7 flex items-center justify-between">
        <FsLogo size={42} />
        <span className="font-mono text-bone-dim text-[11px] sm:text-xs tracking-[0.24em]">
          FASCICOLO N&deg;30 / RISERVATO
        </span>
      </header>

      {/* Center */}
      <div className="relative z-10 flex-1 flex items-center px-6 sm:px-10">
        <div className="w-full max-w-[1280px] mx-auto">
          <div className="max-w-3xl">
            <motion.span
              initial={reduce ? false : { opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
              className="kicker mb-7"
            >
              OGGETTO: I 30 ANNI DI FABIO
            </motion.span>

            <motion.h1
              initial={reduce ? false : { opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.9, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
              className="font-display leading-[0.86] text-bone"
              style={{ fontSize: "clamp(2.9rem, 11vw, 6.5rem)", letterSpacing: "0.005em" }}
            >
              OPERAZIONE
              <br />
              TRENTA<span className="text-red">.</span>
            </motion.h1>

            <motion.p
              initial={reduce ? false : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.32, ease: [0.16, 1, 0.3, 1] }}
              className="mt-7 text-bone-dim text-base sm:text-lg leading-relaxed max-w-xl"
            >
              Un viaggio per i miei trent&apos;anni. Volo e hotel gi&agrave; pagati,
              destinazione finalmente svelata.
            </motion.p>

            <motion.p
              initial={reduce ? false : { opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.7, delay: 0.46 }}
              className="mt-3 font-mono italic text-red text-sm sm:text-base"
            >
              &ldquo;Preparate il fegato, al resto penso io.&rdquo;
            </motion.p>

            <motion.div
              initial={reduce ? false : { opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="mt-9 flex flex-col sm:flex-row gap-3"
            >
              <button onClick={() => navigate("/destination")} data-testid="cta-destination-btn" className="btn btn-primary px-8 py-4">
                Scopri la destinazione
                <ArrowRight size={16} strokeWidth={2} />
              </button>
              <button onClick={onDetails} data-testid="cta-details-btn" className="btn btn-ghost px-8 py-4">
                Apri il fascicolo
              </button>
            </motion.div>
          </div>
        </div>
      </div>

      {/* Foot strip */}
      <div className="relative z-10 w-full max-w-[1280px] mx-auto px-6 sm:px-10 pb-7">
        <div className="rule-dotted mb-4" />
        <div className="flex items-center justify-between font-mono text-[10px] sm:text-[11px] tracking-[0.24em] text-bone-faint">
          <span>FINESTRA: 05-07 FEB 2027</span>
          <span className="hidden sm:inline">ACCESSO: SOLO SU INVITO</span>
        </div>
      </div>
    </section>
  );
}

/* ===================================================== */
/* DESTINATION (redacted)                                */
/* ===================================================== */
function Destination({ sectionRef }) {
  const reduce = useReducedMotion();
  const navigate = useNavigate();
  const intel = [
    { k: "STATO", v: "DECLASSIFICATO", red: true },
    { k: "DIVULGAZIONE", v: "DISPONIBILE ORA" },
    { k: "DESTINAZIONE", v: "NEL FASCICOLO" },
    { k: "VOLO", v: "COPERTO" },
    { k: "ALLOGGIO", v: "COPERTO" },
  ];

  return (
    <section
      ref={sectionRef}
      data-testid="details-section"
      className="relative px-6 sm:px-10 py-24 sm:py-36 border-t border-line"
    >
      <div className="max-w-[1280px] mx-auto grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
        {/* Statement */}
        <motion.div
          variants={fadeUp}
          initial={reduce ? false : "hidden"}
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          className="lg:col-span-7"
        >
          <h2
            className="font-display leading-[0.95] text-bone"
            style={{ fontSize: "clamp(2.1rem, 5.2vw, 3.6rem)" }}
          >
            LA DESTINAZIONE &Egrave;
            <br />
            <Classified label="CLASSIFICATA" className="mt-3" />
          </h2>

          <p className="mt-8 text-bone-dim text-lg leading-relaxed max-w-xl">
            Era. Il fascicolo &egrave; appena stato declassificato: dentro trovi dove
            andiamo, gli orari dei voli e il programma di tutto quello che combineremo.
          </p>
          <p className="mt-4 font-mono italic text-bone-faint text-sm max-w-xl leading-relaxed">
            &ldquo;Un segreto meglio custodito della ricetta del pan di spagna di mia nonna.&rdquo;
          </p>

          <button
            onClick={() => navigate("/destination")}
            data-testid="cta-discover-btn"
            className="btn btn-primary px-8 py-4 mt-9"
          >
            Scopri la destinazione
            <ArrowRight size={16} strokeWidth={2} />
          </button>
        </motion.div>

        {/* Intel readout */}
        <motion.div
          variants={fadeUp}
          custom={1}
          initial={reduce ? false : "hidden"}
          whileInView="visible"
          viewport={{ once: true, amount: 0.3 }}
          className="lg:col-span-5 bg-ink-2 border border-line p-8 sm:p-10"
        >
          <div className="flex items-center gap-3 mb-7">
            <Lock size={16} className="text-red" strokeWidth={1.6} />
            <span className="field-label">SCHEDA INTEL</span>
          </div>
          <dl className="space-y-0">
            {intel.map((row, i) => (
              <div
                key={row.k}
                className={`flex items-center justify-between py-4 ${i !== 0 ? "border-t border-line" : ""}`}
              >
                <dt className="field-label">{row.k}</dt>
                <dd
                  className={`font-mono text-sm tracking-wider ${row.red ? "text-red" : "text-bone"}`}
                >
                  {row.v === "COPERTO" ? (
                    <span className="inline-flex items-center gap-1.5">
                      <Check size={13} strokeWidth={2.4} className="text-red" /> COPERTO
                    </span>
                  ) : (
                    row.v
                  )}
                </dd>
              </div>
            ))}
          </dl>
        </motion.div>
      </div>
    </section>
  );
}

/* ===================================================== */
/* MISSION CLOCK (countdown + the one tape marquee)      */
/* ===================================================== */
function MissionClock({ countdown }) {
  const reduce = useReducedMotion();
  const units = [
    { value: countdown.days, label: "GIORNI" },
    { value: countdown.hours, label: "ORE" },
    { value: countdown.minutes, label: "MINUTI" },
    { value: countdown.seconds, label: "SECONDI" },
  ];
  const tape = ["TOP SECRET", "OPERAZIONE TRENTA", "RISERVATO", "05.02.2027", "FABIO 30"];

  return (
    <section data-testid="countdown-section" className="relative overflow-hidden border-t border-line">
      {/* The single marquee on the page */}
      <div className="tape" aria-hidden="true">
        <div className="tape-track">
          {[0, 1].map((dup) => (
            <span key={dup} className="inline-flex items-center">
              {tape.map((t, i) => (
                <span key={`${dup}-${i}`} className="inline-flex items-center">
                  <span className="tape-item">{t}</span>
                  <span className="tape-dot">&bull;</span>
                </span>
              ))}
            </span>
          ))}
        </div>
      </div>

      <div className="px-6 sm:px-10 py-24 sm:py-32">
        <div className="max-w-[1280px] mx-auto">
          <motion.div
            variants={fadeUp}
            initial={reduce ? false : "hidden"}
            whileInView="visible"
            viewport={{ once: true, amount: 0.4 }}
            className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-14"
          >
            <h2 className="font-display text-bone leading-none" style={{ fontSize: "clamp(2.2rem, 5vw, 3.5rem)" }}>
              CONTO ALLA ROVESCIA
            </h2>
            <span className="font-mono text-red text-lg sm:text-xl tracking-[0.18em]">05 / 02 / 2027</span>
          </motion.div>

          {countdown.arrived ? (
            <motion.h3
              initial={reduce ? false : { opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
              data-testid="countdown-revealed"
              className="font-display text-red text-4xl sm:text-6xl tracking-wider"
            >
              L&apos;OPERAZIONE &Egrave; INIZIATA
            </motion.h3>
          ) : (
            <motion.div
              variants={fadeUp}
              custom={1}
              initial={reduce ? false : "hidden"}
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              data-testid="countdown-timer"
              className="grid grid-cols-2 sm:grid-cols-4 gap-px bg-line border border-line"
            >
              {units.map((u) => (
                <div key={u.label} className="bg-ink px-4 py-8 sm:py-12 flex flex-col items-center">
                  <span
                    className="font-mono font-bold text-bone leading-none tabular-nums"
                    style={{ fontSize: "clamp(3rem, 9vw, 6rem)" }}
                    data-testid={`countdown-${u.label.toLowerCase()}`}
                  >
                    {String(u.value).padStart(2, "0")}
                  </span>
                  <span className="field-label mt-5">{u.label}</span>
                </div>
              ))}
            </motion.div>
          )}

          <motion.p
            initial={reduce ? false : { opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true, amount: 0.5 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-10 font-mono italic text-bone-faint text-sm text-center sm:text-left"
          >
            &ldquo;30 anni e ancora finanziariamente irresponsabile.&rdquo;
          </motion.p>
        </div>
      </div>
    </section>
  );
}

/* ===================================================== */
/* BRIEFING (numbered dossier + duotone objective)       */
/* ===================================================== */
function Briefing() {
  const reduce = useReducedMotion();
  const items = [
    { n: "01", Icon: Calendar, t: "LE DATE", b: "5-7 Febbraio 2027. Tre giorni, zero preoccupazioni. Porta il sorriso, lascia a casa l’ansia." },
    { n: "02", Icon: MapPin, t: "LA DESTINAZIONE", b: "Non è più un segreto. Apri il fascicolo declassificato per scoprirla, con voli e programma." },
    { n: "03", Icon: Plane, t: "IL VOLO", b: "A carico di Fabio. Tu pensa solo a presentarti in tempo, con il documento giusto in tasca." },
    { n: "04", Icon: BedDouble, t: "L’ALLOGGIO", b: "Anche questo è coperto. Niente «devo prima sentire il mio conto in banca»." },
  ];

  return (
    <section data-testid="info-section" className="relative px-6 sm:px-10 py-24 sm:py-36 border-t border-line">
      <div className="max-w-[1280px] mx-auto">
        <motion.h2
          variants={fadeUp}
          initial={reduce ? false : "hidden"}
          whileInView="visible"
          viewport={{ once: true, amount: 0.4 }}
          className="font-display text-bone leading-none mb-14"
          style={{ fontSize: "clamp(2.4rem, 6vw, 4.5rem)" }}
        >
          IL BRIEFING
        </motion.h2>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Numbered dossier rows */}
          <div className="lg:col-span-7">
            {items.map((it, i) => (
              <motion.div
                key={it.n}
                variants={fadeUp}
                custom={i}
                initial={reduce ? false : "hidden"}
                whileInView="visible"
                viewport={{ once: true, amount: 0.4 }}
                className={`flex gap-6 sm:gap-8 py-7 ${i !== 0 ? "border-t border-line" : ""}`}
              >
                <span className="font-mono text-red text-sm pt-1 tracking-widest shrink-0">{it.n}</span>
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-3">
                    <it.Icon size={20} className="text-bone" strokeWidth={1.5} />
                    <h3 className="font-display text-bone text-2xl sm:text-3xl tracking-wide">{it.t}</h3>
                  </div>
                  <p className="text-bone-dim leading-relaxed max-w-md">{it.b}</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Objective image (duotone) */}
          <motion.figure
            variants={fadeUp}
            custom={1}
            initial={reduce ? false : "hidden"}
            whileInView="visible"
            viewport={{ once: true, amount: 0.3 }}
            className="lg:col-span-5 lg:sticky lg:top-12"
          >
            <div className="duotone aspect-[4/5] border border-line">
              <img src={summitImg} alt="Ricostruzione artistica della missione" />
              <figcaption className="absolute inset-x-0 bottom-0 p-6 z-10">
                <span className="field-label text-bone">RICOSTRUZIONE ARTISTICA</span>
                <p className="font-mono text-bone-dim text-xs mt-2">
                  (no, non &egrave; la vera destinazione)
                </p>
              </figcaption>
            </div>
            <div className="bg-ink-2 border border-line border-t-0 p-6">
              <span className="field-label">COSA PORTARE</span>
              <p className="mt-3 text-bone-dim leading-relaxed">
                Passaporto, senso dell&apos;avventura e un fegato che ti voglia ancora bene.
                L&apos;ansia lasciala a casa.
              </p>
            </div>
          </motion.figure>
        </div>
      </div>
    </section>
  );
}

/* ===================================================== */
/* FOOTER                                                */
/* ===================================================== */
function Footer() {
  const navigate = useNavigate();
  return (
    <footer data-testid="footer" className="relative px-6 sm:px-10 pt-16 pb-10 border-t border-line">
      <div className="max-w-[1280px] mx-auto flex flex-col items-center text-center gap-6">
        <FsLogo size={48} />
        <div className="rule-dotted max-w-xs" />
        <div>
          <p className="font-mono text-bone tracking-[0.24em] text-sm">
            OPERAZIONE TRENTA / 05-07 FEB 2027
          </p>
          <p className="font-mono italic text-bone-faint text-sm mt-3 max-w-md">
            Con amore, Fabio. 30 anni e ancora nessuna idea di cosa fare da grande.
          </p>
        </div>
        <button
          onClick={() => navigate("/admin")}
          className="font-mono text-bone-faint hover:text-red transition-colors tracking-[0.24em] text-[11px] mt-1"
        >
          MISSION CONTROL
        </button>
      </div>
    </footer>
  );
}
